import { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { MdEdit, MdDelete, MdVisibility, MdClose, MdSearch, MdCheckCircle } from 'react-icons/md';
import ImageCropper from '../components/ImageCropper';
import WebPreviewModal from '../components/WebPreviewModal';
import Pagination from '../components/Pagination';
import { formatDate } from '../utils/format';
import { toEnglishAuthorName } from '../utils/authorUtils';

function SearchableSelect({ label, value, options, onChange, placeholder = "Search...", searchKey = "slug" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = options.filter(opt =>
    (opt.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (opt[searchKey] || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="form-group">
      <label>{label}</label>
      <div className="searchable-select">
        <div className="select-box" onClick={() => setIsOpen(!isOpen)}>
          {(typeof value === 'string' && (value.trim() === 'అల్ ఇండియా' || value.trim() === 'అల్ ఇండియా ') ? 'All India' : value) || `Select ${label}`}
        </div>
        {isOpen && (
          <div className="select-dropdown">
            <input
              autoFocus
              placeholder={placeholder}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
            />
            <div className="options-list">
              {filtered.map(opt => (
                <div
                  key={opt.id || opt.name}
                  className="option-item"
                  onClick={() => {
                    onChange(opt.name);
                    setIsOpen(false);
                    setSearch('');
                  }}
                >
                  <span>{(opt.name?.trim() === 'అల్ ఇండియా' || opt.name?.trim() === 'అల్ ఇండియా ') ? 'All India' : opt.name}</span>
                  {opt[searchKey] && <span className="text-muted text-xs">({opt[searchKey]})</span>}
                </div>
              ))}
              {filtered.length === 0 && <div className="option-item text-muted">No results found</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ReviewArticles() {
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [users, setUsers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [filters, setFilters] = useState({ category: '', city: '', admin_id: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const stateMapping = {
    'ఆంధ్రప్రదేశ్': 'Andhra Pradesh',
    'తెలంగాణ': 'Telangana',
    'అల్ ఇండియా': 'All India',
    'అల్ ఇండియా ': 'All India',
    'ఆంధ్ర ప్రదేశ్': 'Andhra Pradesh',
    'Telangana': 'Telangana',
    'Andhra Pradesh': 'Andhra Pradesh'
  };
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({});

  // View modal
  const [viewModal, setViewModal] = useState(null);

  // Delete confirmation modal
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Approve confirmation modal
  const [approveModal, setApproveModal] = useState(null);
  const [approving, setApproving] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [viewMatchedArticle, setViewMatchedArticle] = useState(null);

  // Schedule modal
  const [scheduleModal, setScheduleModal] = useState(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduling, setScheduling] = useState(false);

  const handlePreApprove = async (article) => {
    // Duplicate-check feature disabled for now — commented out, not removed.
    // setCheckingDuplicate(true);
    // try {
    //   const res = await api.post('/admin/articles/check-duplicate', {
    //     title: article.title,
    //     summary: article.summary,
    //     category: article.category
    //   });
    //   if (res.data.success && res.data.has_duplicates) {
    //     setDuplicateWarning({ article, duplicates: res.data.duplicates });
    //   } else {
    //     setApproveModal(article);
    //   }
    // } catch (err) {
    //   setApproveModal(article);
    // } finally {
    //   setCheckingDuplicate(false);
    // }
    setApproveModal(article);
  };


  const fetchArticles = useCallback(async (page = 1) => {
    setLoading(true);
    window.scrollTo(0, 0);
    try {
      const params = { page, limit: 15, status: 'draft' };
      if (filters.category) params.category = filters.category;
      if (filters.city) params.city = filters.city;
      if (filters.admin_id) params.admin_id = filters.admin_id;
      if (searchQuery.trim()) params.search = searchQuery.trim();
      const res = await api.get('/admin/articles', { params });
      setArticles(res.data.data);
      setPagination(res.data.pagination);
    } catch (err) {
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  }, [filters, searchQuery]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchArticles(1);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, filters, fetchArticles]);

  useEffect(() => {
    api.get('/categories').then(res => setCategories(res.data.data)).catch(() => { });
    api.get('/cities').then(res => setCities(res.data.data)).catch(() => { });
    api.get('/admin/admins').then(res => setUsers(res.data.data)).catch(() => { });
  }, []);

  const filteredArticles = articles;

  const handleAiGenerate = async (type, extraData = null) => {
    if (type === 'slug' && !editForm.title) {
      return toast.error('Please enter a title first');
    }
    if (type === 'transliterate' && !extraData) {
      return toast.error('Please enter some text to convert');
    }

    setLoading(true);
    try {
      const payload = { type };
      if (type === 'slug') payload.title = editForm.title;
      if (type === 'transliterate') payload.text = extraData;

      const res = await api.post('/admin/ai/generate', payload);
      if (res.data.success) {
        if (type === 'slug') setEditForm(f => ({ ...f, slug: res.data.data }));
        if (type === 'author') setEditForm(f => ({ ...f, author_name: res.data.data }));
        if (type === 'transliterate') {
          return res.data.data;
        }
        toast.success('Generated');
      }
    } catch (err) {
      toast.error('AI operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleTransliterateEdit = async (field) => {
    const text = editForm[field];
    if (!text) return toast.error(`Please type something in ${field} first`);
    const result = await handleAiGenerate('transliterate', text);
    if (result) {
      const limit = field === 'title' ? 75 : field === 'summary' ? 400 : result.length;
      setEditForm(f => ({ ...f, [field]: result.slice(0, limit) }));
    }
  };

  const confirmDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/articles/${deleteModal.id}`);
      toast.success('Article rejected and deleted');
      setDeleteModal(null);
      fetchArticles(pagination.page);
    } catch (err) {
      toast.error('Reject failed');
    } finally {
      setDeleting(false);
    }
  };

  const confirmApprove = async () => {
    if (!approveModal) return;
    setApproving(true);
    try {
      await api.put(`/admin/articles/${approveModal.id}`, { status: 'published' });
      toast.success('Article Approved & Published');
      setApproveModal(null);
      fetchArticles(pagination.page);
    } catch (err) {
      toast.error('Approve failed');
    } finally {
      setApproving(false);
    }
  };

  const confirmSchedule = async () => {
    if (!scheduleModal || !scheduleDate) return toast.error('Please select a date and time');
    setScheduling(true);
    try {
      const utcDate = new Date(scheduleDate).toISOString();
      await api.put(`/admin/articles/${scheduleModal.id}`, { status: 'scheduled', published_at: utcDate });
      toast.success('Article Scheduled');
      setScheduleModal(null);
      fetchArticles(pagination.page);
    } catch (err) {
      toast.error('Schedule failed');
    } finally {
      setScheduling(false);
    }
  };

  const [editUploading, setEditUploading] = useState(false);
  const [showEditWebPreview, setShowEditWebPreview] = useState(false);
  const [editCropper, setEditCropper] = useState({ active: false, image: null, type: null });

  const [editState, setEditState] = useState('');
  const [editCities, setEditCities] = useState([]);

  const openEdit = (article) => {
    setEditModal(article);
    const cityObj = cities.find(c => c.name === article.city);
    const initialState = cityObj ? cityObj.state : '';
    setEditState(initialState);
    setEditCities(cities.filter(c => c.state === initialState));

    setEditForm({
      title: article.title || '',
      summary: article.summary || '',
      category: article.category || '',
      city: article.city || '',
      source_url: article.source_url || '',
      source: article.source || '',
      media_type: article.media_type || 'image',
      image_url: article.image_url || '',
      thumbnail_url: article.thumbnail_url || '',
      video_url: article.video_url || '',
      is_featured: article.is_featured || false,
      slug: (article.slug || '').split('-').slice(0, -1).join('-'),
      author_name: article.author_name || '',
      images: article.images ? article.images.map(img => img.image_url) : [],
      status: article.status || 'draft',
      published_at: article.published_at || '',
      manual_thumbnail: false,
    });
  };

  const handleEditFileSelect = (fileOrType, type) => {
    if (typeof fileOrType === 'string') {
      const currentType = fileOrType;
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = currentType === 'video' ? 'video/mp4' : 'image/*';
      input.onchange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const selectedFile = files[0];
        if (currentType === 'video') {
          uploadEditToServer(selectedFile, currentType);
        } else {
          const reader = new FileReader();
          reader.onload = () => {
            setEditCropper({ active: true, image: reader.result, type: currentType });
          };
          reader.readAsDataURL(selectedFile);
        }
      };
      input.click();
      return;
    }

    const file = fileOrType;
    if (type === 'video') {
      uploadEditToServer(file, type);
    } else {
      if (file && file instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          setEditCropper({ active: true, image: reader.result, type });
        };
        reader.readAsDataURL(file);
      }
    }
  };

  useEffect(() => {
    const handleGlobalPaste = (e) => {
      if (!editModal) return;
      if (editForm?.media_type !== 'image') return;
      if (editCropper.active || showEditWebPreview) return;

      const items = e.clipboardData?.items || [];
      for (const it of items) {
        if (it.type && it.type.startsWith('image/')) {
          const file = it.getAsFile();
          if (file) {
            e.preventDefault();
            const reader = new FileReader();
            reader.onload = () => {
              setEditCropper({ active: true, image: reader.result, type: 'image' });
            };
            reader.readAsDataURL(file);
            return;
          }
        }
      }
    };

    window.addEventListener('paste', handleGlobalPaste);
    return () => window.removeEventListener('paste', handleGlobalPaste);
  }, [editModal, editForm?.media_type, editCropper.active, showEditWebPreview]);

  const handleEditPasteImage = (e) => {
    e.preventDefault();
  };

  const handleEditCropComplete = async (blob) => {
    const type = editCropper.type;
    setEditCropper({ active: false, image: null, type: null });

    const file = new File([blob], `cropped-${Date.now()}.jpg`, { type: 'image/jpeg' });
    uploadEditToServer(file, type);
  };

  const uploadEditToServer = async (file, type) => {
    const formData = new FormData();
    formData.append('file', file);
    const endpoint = (type === 'image' || type === 'extra_image') ? '/admin/upload/image'
      : type === 'thumbnail' ? '/admin/upload/thumbnail'
        : '/admin/upload/video';

    setEditUploading(true);
    try {
      const res = await api.post(endpoint, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = res.data.data.url;
      const thumbnailUrl = res.data.data.thumbnail_url;

      if (type === 'image') setEditForm(f => ({ ...f, image_url: url }));
      else if (type === 'extra_image') setEditForm(f => ({ ...f, images: [...(f.images || []), url] }));
      else if (type === 'thumbnail') {
        setEditForm(f => ({ ...f, thumbnail_url: url, manual_thumbnail: true }));
      }
      else if (type === 'video') {
        setEditForm(f => {
          const nextForm = { ...f, video_url: url };
          if (thumbnailUrl && !f.manual_thumbnail) {
            nextForm.thumbnail_url = thumbnailUrl;
          }
          return nextForm;
        });
      }
      toast.success(`${type === 'extra_image' ? 'Image' : type} uploaded and resized!`);
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setEditUploading(false);
    }
  };

  const saveEdit = async () => {
    if (!editModal) return;
    try {
      await api.put(`/admin/articles/${editModal.id}`, editForm);
      toast.success('Article updated');
      setEditModal(null);
      fetchArticles(pagination.page);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    }
  };

  const handleEditSubmit = (e) => {
    if (e) e.preventDefault();
    if (!editForm.title || !editForm.summary) return toast.error('Title and summary required');
    if (editForm.media_type === 'image' && (!editForm.image_url || !editForm.image_url.trim())) {
      return toast.error('Please upload the Article Image (Web & App).');
    }
    if (editForm.media_type === 'video') {
      if (!editForm.video_url || !editForm.video_url.trim()) {
        return toast.error('Please upload the Article Video.');
      }
      if (!editForm.thumbnail_url || !editForm.thumbnail_url.trim()) {
        return toast.error('Please upload the Article Video Thumbnail.');
      }
    }
    saveEdit();
  };

  return (
    <div className="page">
      <div className="page-header">
        <h2>Review Articles</h2>
        <p className="text-muted">Review drafts submitted by reporters before publishing.</p>
      </div>

      <div className="filter-bar">
        <div className="search-input-wrap">
          <MdSearch className="search-icon" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search articles by title..."
            className="search-input"
          />
        </div>
        <select value={filters.category} onChange={(e) => setFilters(f => ({ ...f, category: e.target.value }))}>
          <option value="">All Categories</option>
          {categories.map(c => <option key={c.slug} value={c.name}>{c.name}</option>)}
        </select>
        <select value={filters.city} onChange={(e) => setFilters(f => ({ ...f, city: e.target.value }))}>
          <option value="">All Cities</option>
          {cities.map(c => <option key={c.slug} value={c.name}>{c.name}</option>)}
        </select>
        <select value={filters.admin_id} onChange={(e) => setFilters(f => ({ ...f, admin_id: e.target.value }))}>
          <option value="">All Users</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>

      {loading ? <div className="page-loader">Loading...</div> : (
        <>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Reporter</th>
                  <th className="hide-mobile">Category</th>
                  <th className="hide-mobile">City</th>
                  <th>Date</th>
                  <th>Scheduled Publish</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredArticles.map(a => (
                  <tr key={a.id}>
                    <td className="title-cell">
                      <span className="article-title">{a.title}</span>
                    </td>
                    <td>
                      <span className="text-sm">
                        {a.creator ? `${a.creator.name} (${a.creator.role})` : 'Unknown'}
                      </span>
                    </td>
                    <td className="hide-mobile">
                      <span className="badge badge-category">{a.category?.replace('_', ' ')}</span>
                    </td>
                    <td className="hide-mobile">
                      <span className="badge">{a.city?.replace('_', ' ')}</span>
                    </td>
                    <td>{formatDate(a.published_at)}</td>
                    <td>{a.status === 'scheduled' ? formatDate(a.published_at) : '-'}</td>
                    <td>
                      <div className="action-btns">
                        <button className="btn btn-sm btn-ghost" onClick={() => setViewModal(a)} title="View & Review"><MdVisibility /></button>
                        <button className="btn btn-sm btn-ghost" onClick={() => openEdit(a)} title="Edit"><MdEdit /></button>
                        <button className="btn btn-sm" style={{ color: 'var(--blue)', borderColor: 'var(--blue)' }} onClick={() => setScheduleModal(a)} title="Schedule Publish">Schedule</button>
                        <button className="btn btn-sm" style={{ color: 'var(--green)', borderColor: 'var(--green)' }} onClick={() => handlePreApprove(a)} disabled={checkingDuplicate} title="Approve & Publish">{checkingDuplicate ? '...' : <MdCheckCircle />}</button>
                        <button className="btn btn-sm btn-danger" onClick={() => setDeleteModal(a)} title="Reject"><MdDelete /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredArticles.length === 0 && (
                  <tr><td colSpan="6" className="empty-row">No drafts pending review.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination pagination={pagination} onPageChange={fetchArticles} />
        </>
      )}

      {/* View Article Modal */}
      {viewModal && (
        <div className="modal-overlay" onClick={() => setViewModal(null)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '16px', paddingBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                <h2 style={{ margin: 0, fontFamily: 'Ramabhadra, serif', fontSize: '28px', fontWeight: '400', color: '#111827', flex: 1 }}>{viewModal.title}</h2>
                <button className="modal-close" onClick={() => setViewModal(null)}><MdClose /></button>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: '20px' }}>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <span className="badge badge-category">{viewModal.category?.replace('_', ' ')}</span>
                  <span className="badge badge-category">Draft</span>
                  <span className="badge">{viewModal.city?.replace('_', ' ')}</span>
                </div>
              </div>
            </div>
            <div className="modal-body modal-grid-2col" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <div className="detail-row"><span className="detail-label">Reporter</span><span>{viewModal.creator ? `${viewModal.creator.name} (${viewModal.creator.role})` : 'Unknown'}</span></div>
              <div className="detail-row"><span className="detail-label">Summary</span><span style={{ whiteSpace: 'pre-wrap' }}>{viewModal.summary}</span></div>

              {viewModal.author_name && (
                <div className="detail-row"><span className="detail-label">Author</span><span>{toEnglishAuthorName(viewModal.author_name)}</span></div>
              )}
              {viewModal.source && (
                <div className="detail-row"><span className="detail-label">Source</span><span>{viewModal.source}</span></div>
              )}
              {viewModal.source_url && (
                <div className="detail-row"><span className="detail-label">Source URL</span><a href={viewModal.source_url} target="_blank" rel="noreferrer" className="detail-link">{viewModal.source_url}</a></div>
              )}
              {viewModal.media_type === 'video' && viewModal.video_url ? (
                <div className="detail-row">
                  <span className="detail-label">Video</span>
                  <video
                    src={viewModal.video_url}
                    poster={viewModal.thumbnail_url}
                    controls
                    className="detail-img"
                    style={{ objectFit: 'contain', background: '#000', maxHeight: '350px', width: '100%' }}
                  />
                </div>
              ) : (
                viewModal.image_url && (
                  <div className="detail-row"><span className="detail-label">Image</span><img src={viewModal.image_url} alt="" className="detail-img" /></div>
                )
              )}
              {viewModal.images && viewModal.images.length > 0 && (
                <div className="detail-row">
                  <span className="detail-label">Gallery</span>
                  <div className="flex-row gap-2 flex-wrap">
                    {viewModal.images.map((img, i) => (
                      <img key={i} src={img.image_url} alt="" className="detail-img" style={{ width: 60, height: 60, objectFit: 'cover', margin: 0 }} />
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ justifyContent: 'flex-end', gap: '10px' }}>
              <button
                className="btn btn-danger"
                onClick={() => { setViewModal(null); setDeleteModal(viewModal); }}
              >
                Reject & Delete
              </button>
              <button
                className="btn btn-primary"
                style={
                  viewModal.status !== 'draft'
                    ? { background: '#E5E7EB', color: '#6B7280', borderColor: '#E5E7EB', cursor: 'not-allowed', opacity: 0.7 }
                    : { background: '#3B82F6', borderColor: '#3B82F6', color: '#FFFFFF' }
                }
                disabled={viewModal.status !== 'draft'}
                onClick={() => { setViewModal(null); setScheduleModal(viewModal); }}
              >
                Schedule Publish
              </button>
              <button
                className="btn btn-primary"
                onClick={() => { setViewModal(null); handlePreApprove(viewModal); }}
              >
                Approve & Publish
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Article Modal */}
      {editModal && (
        <div className="modal-overlay" onClick={() => setEditModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Article</h3>
              <div className="flex-row items-center gap-4">
                <button
                  type="button"
                  className="btn btn-xs btn-ghost"
                  style={{ color: 'var(--primary)', border: '1px solid var(--primary)', padding: '6px 12px' }}
                  onClick={() => setShowEditWebPreview(true)}
                >
                  <MdVisibility /> Website Preview
                </button>
                <button className="modal-close" onClick={() => setEditModal(null)}><MdClose /></button>
              </div>
            </div>
            <div className="modal-body">
              <form className="modal-form" onSubmit={handleEditSubmit}>
                <div className="form-group">
                  <div className="flex-row justify-between items-center mb-1">
                    <label className="mb-0">Title <span className="char-count">{(editForm.title || '').length}/75</span></label>
                    <button type="button" className="btn btn-xs btn-ghost ai-btn" onClick={() => handleTransliterateEdit('title')} disabled={loading}>
                      ✨ Convert to Telugu
                    </button>
                  </div>
                  <input value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value.slice(0, 75) }))} maxLength={75} required />
                </div>

                <div className="form-group">
                  <div className="flex-row justify-between items-center mb-1">
                    <label className="mb-0">URL Slug (English)</label>
                    <button type="button" className="btn btn-xs btn-ghost ai-btn" onClick={async () => {
                      setLoading(true);
                      try {
                        const res = await api.post('/admin/ai/generate', { type: 'slug', title: editForm.title });
                        if (res.data.success) setEditForm(f => ({ ...f, slug: res.data.data }));
                      } catch (err) { toast.error('Slug generation failed'); }
                      finally { setLoading(false); }
                    }} disabled={loading}>
                      ✨ AI Generate
                    </button>
                  </div>
                  <input value={editForm.slug} onChange={e => setEditForm(f => ({ ...f, slug: e.target.value }))} placeholder="e.g. latest-news" />
                </div>

                <div className="form-group">
                  <div className="flex-row justify-between items-center mb-1">
                    <label className="mb-0">Summary <span className="char-count">{(editForm.summary || '').length}/400</span></label>
                    <button type="button" className="btn btn-xs btn-ghost ai-btn" onClick={() => handleTransliterateEdit('summary')} disabled={loading}>
                      ✨ Convert to Telugu
                    </button>
                  </div>
                  <textarea value={editForm.summary} onChange={e => setEditForm(f => ({ ...f, summary: e.target.value.slice(0, 400) }))} rows={4} maxLength={400} required />
                </div>

                {/* Media */}
                <div className="form-group">
                  <label>Media Type</label>
                  <div className="radio-group">
                    <label className={`radio-card ${editForm.media_type === 'image' ? 'selected' : ''}`}>
                      <input type="radio" value="image" checked={editForm.media_type === 'image'}
                        onChange={() => setEditForm(f => ({ ...f, media_type: 'image' }))} /> Image
                    </label>
                    <label className={`radio-card ${editForm.media_type === 'video' ? 'selected' : ''}`}>
                      <input type="radio" value="video" checked={editForm.media_type === 'video'}
                        onChange={() => setEditForm(f => ({ ...f, media_type: 'video' }))} /> Video
                    </label>
                  </div>
                </div>

                {editForm.media_type === 'image' && (
                  <>
                    <div className="form-group">
                      <label>Article Image (Web & App)</label>
                      <div className="upload-area" tabIndex={0} onClick={() => handleEditFileSelect('image')} onPaste={handleEditPasteImage}>
                        {editForm.image_url ? (
                          <img src={editForm.image_url} alt="preview" className="upload-preview" />
                        ) : (
                          <p>{editUploading ? 'Processing...' : 'Click to select and crop image'}</p>
                        )}
                      </div>
                      <input type="text" value={editForm.image_url}
                        onChange={e => setEditForm(f => ({ ...f, image_url: e.target.value }))}
                        placeholder="Or paste image URL" className="mt-sm" />
                    </div>
                    <div className="form-group border-t border-gray-200 pt-4 mt-4">
                      <label>Additional Images (Gallery)</label>
                      <div className="flex-row gap-4 flex-wrap mb-sm">
                        {(editForm.images || []).map((imgUrl, i) => (
                          <div key={i} style={{ position: 'relative', width: 80, height: 80 }}>
                            <img src={imgUrl} alt={`extra-${i}`} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} />
                            <button type="button" onClick={() => setEditForm(prev => ({ ...prev, images: prev.images.filter((_, index) => index !== i) }))} style={{ position: 'absolute', top: -5, right: -5, background: 'red', color: 'white', borderRadius: '50%', width: 20, height: 20, border: 'none', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>X</button>
                          </div>
                        ))}
                        <div
                          onClick={() => handleEditFileSelect('extra_image')}
                          style={{ width: 80, height: 80, border: '2px dashed #ccc', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: '#fafafa' }}
                        >
                          <span style={{ fontSize: 24, color: '#999' }}>+</span>
                        </div>
                      </div>
                      <p className="text-muted text-xs">Upload multiple images to create an image gallery for this article.</p>
                    </div>
                  </>
                )}

                {editForm.media_type === 'video' && (
                  <>
                    <div className="form-group">
                      <label>Video URL</label>
                      <input type="text" value={editForm.video_url} onChange={e => setEditForm(f => ({ ...f, video_url: e.target.value }))} placeholder="MP4 URL..." />
                      <button type="button" className="btn btn-xs mt-xs" onClick={() => handleEditFileSelect('video')}>Upload Video</button>
                    </div>
                    <div className="form-group">
                      <label>Video Thumbnail (App & Web)</label>
                      <div className="upload-area" onClick={() => handleEditFileSelect('thumbnail')}>
                        {editForm.thumbnail_url ? (
                          <img src={editForm.thumbnail_url} alt="preview" className="upload-preview" />
                        ) : (
                          <p>{editUploading ? 'Processing...' : 'Click to select and crop thumbnail'}</p>
                        )}
                      </div>
                      <input type="text" value={editForm.thumbnail_url} onChange={e => setEditForm(f => ({ ...f, thumbnail_url: e.target.value }))} placeholder="Or paste thumbnail URL" className="mt-sm" />
                    </div>
                  </>
                )}

                <div className="form-group">
                  <div className="flex-row justify-between items-center mb-1">
                    <label className="mb-0">Author Name</label>
                    <button type="button" className="btn btn-xs btn-ghost ai-btn" onClick={() => handleAiGenerate('author')} disabled={loading}>
                      ✨ AI Random Name
                    </button>
                  </div>
                  <input type="text" value={editForm.author_name} onChange={e => setEditForm(f => ({ ...f, author_name: e.target.value }))} placeholder="e.g. Rajesh Kumar..." />
                </div>
                <div className="form-row">
                  <SearchableSelect
                    label="Category"
                    value={editForm.category}
                    options={categories}
                    onChange={(val) => setEditForm(f => ({ ...f, category: val }))}
                  />
                  <div className="form-group">
                    <label>State</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', minHeight: '42px' }}>
                      {['Andhra Pradesh', 'Telangana', 'All India'].map(stateName => {
                        let isSelected = false;
                        if (editState) {
                          const sTrim = editState.trim();
                          if (sTrim === stateName || (stateName === 'Andhra Pradesh' && (sTrim === 'ఆంధ్రప్రదేశ్' || sTrim === 'ఆంధ్ర ప్రదేశ్')) || (stateName === 'Telangana' && sTrim === 'తెలంగాణ') || (stateName === 'All India' && (sTrim === 'అల్ ఇండియా' || sTrim === 'అల్ ఇండియా '))) {
                            isSelected = true;
                          }
                        }
                        return (
                          <label key={stateName} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px', color: isSelected ? '#EF233C' : '#333', fontWeight: isSelected ? '500' : '400' }}>
                            <input
                              type="radio"
                              name="edit_quick_state"
                              checked={isSelected}
                              onChange={() => {
                                setEditState(stateName);
                                const filtered = cities.filter(c => {
                                  if (!c.State && !c.state) return false;
                                  const cState = (c.State ? c.State.name : c.state).trim();
                                  return cState === stateName || (stateName === 'Andhra Pradesh' && (cState === 'ఆంధ్రప్రదేశ్' || cState === 'ఆంధ్ర ప్రదేశ్')) || (stateName === 'Telangana' && cState === 'తెలంగాణ') || (stateName === 'All India' && (cState === 'అల్ ఇండియా' || cState === 'అల్ ఇండియా '));
                                });
                                setEditCities(filtered);
                                if (filtered.length > 0) setEditForm(f => ({ ...f, city: filtered[0].name }));
                              }}
                              style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: '#EF233C' }}
                            />
                            {stateName}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  <SearchableSelect
                    label="City"
                    value={editForm.city}
                    options={editCities}
                    onChange={(val) => setEditForm(f => ({ ...f, city: val }))}
                  />
                </div>
                <div className="form-group">
                  <label>Source</label>
                  <input type="text" value={editForm.source} onChange={e => setEditForm(f => ({ ...f, source: e.target.value }))} placeholder="e.g. Times of India..." />
                </div>
                <div className="form-group">
                  <label>Source URL</label>
                  <input type="url" value={editForm.source_url} onChange={e => setEditForm(f => ({ ...f, source_url: e.target.value }))} placeholder="https://..." />
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input type="checkbox" checked={editForm.is_featured}
                      onChange={(e) => setEditForm(f => ({ ...f, is_featured: e.target.checked }))} />
                    <span className="featured-tag">Featured Article</span> — show in hero banner
                  </label>
                </div>

                <div className="form-group">
                  <label>Article Status</label>
                  <select
                    value={editForm.status}
                    onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="scheduled">Scheduled</option>
                  </select>
                </div>
                {editForm.status === 'scheduled' && (
                  <div className="form-group">
                    <label>Schedule Date & Time</label>
                    <input
                      type="datetime-local"
                      value={editForm.published_at ? (() => {
                        const d = new Date(editForm.published_at);
                        if (isNaN(d.getTime())) return '';
                        d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
                        return d.toISOString().slice(0, 16);
                      })() : ''}
                      onChange={e => {
                        if (e.target.value) {
                          setEditForm(f => ({ ...f, published_at: new Date(e.target.value).toISOString() }));
                        } else {
                          setEditForm(f => ({ ...f, published_at: '' }));
                        }
                      }}
                    />
                  </div>
                )}

              </form>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setEditModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEdit}>Save Changes</button>
            </div>
          </div>
        </div>
      )}



      {/* Approve Confirmation Modal */}
      {approveModal && (
        <div className="modal-overlay" onClick={() => setApproveModal(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Approve Article</h3>
              <button className="modal-close" onClick={() => setApproveModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to approve and publish this article?</p>
              <p className="text-muted" style={{ marginTop: 8 }}>"{approveModal.title}"</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setApproveModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmApprove} disabled={approving}>
                {approving ? 'Publishing...' : 'Approve & Publish'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Confirmation Modal */}
      {scheduleModal && (
        <div className="modal-overlay" onClick={() => setScheduleModal(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Schedule Article</h3>
              <button className="modal-close" onClick={() => setScheduleModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p>Select date and time to publish this article automatically.</p>
              <p className="text-muted" style={{ marginTop: 8 }}>"{scheduleModal.title}"</p>
              <div className="form-group" style={{ marginTop: 15 }}>
                <label>Date & Time</label>
                <input
                  type="datetime-local"
                  value={scheduleDate}
                  onChange={e => setScheduleDate(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setScheduleModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmSchedule} disabled={scheduling}>
                {scheduling ? 'Scheduling...' : 'Schedule Publish'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="modal-overlay" onClick={() => setDeleteModal(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reject Article</h3>
              <button className="modal-close" onClick={() => setDeleteModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to reject and delete this draft?</p>
              <p className="text-muted" style={{ marginTop: 8 }}>"{deleteModal.title}"</p>
              <p className="text-muted" style={{ marginTop: 8 }}>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDeleteModal(null)}>Cancel</button>
              <button className="btn btn-danger-solid" onClick={confirmDelete} disabled={deleting}>
                {deleting ? 'Rejecting...' : 'Reject & Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Warning Modal */}
      {duplicateWarning && (
        <div className="modal-overlay" onClick={() => setDuplicateWarning(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 540, borderRadius: 16, overflow: 'hidden' }}>
            <div style={{ background: '#FEF2F2', padding: '20px 24px', borderBottom: '1px solid #FEE2E2', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#FEE2E2', color: '#DC2626', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
                ⚠️
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#991B1B' }}>Duplicate Article Found</h3>
                <p style={{ margin: 0, fontSize: 13, color: '#B91C1C', marginTop: 2 }}>
                  This article is very similar to an existing published article.
                </p>
              </div>
              <button className="modal-close" onClick={() => setDuplicateWarning(null)}>
                <MdClose />
              </button>
            </div>

            <div className="modal-body" style={{ padding: 24, maxHeight: '55vh', overflowY: 'auto' }}>
              <p style={{ fontSize: 13, color: '#64748B', margin: '0 0 16px 0' }}>
                {duplicateWarning.duplicates.length} similar article{duplicateWarning.duplicates.length > 1 ? 's' : ''} found — review before publishing.
              </p>

              {duplicateWarning.duplicates.map((dup) => (
                <div key={dup.id} style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 12, padding: 16, marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {dup.ai_reason ? 'AI Confidence' : 'Similarity Score'}
                    </span>
                    <span style={{ fontSize: 15, fontWeight: 800, color: '#E11D48', padding: '2px 10px', background: '#FFE4E6', borderRadius: 20 }}>
                      {dup.ai_reason ? `${Math.round(dup.ai_confidence * 100)}%` : `${dup.score}%`}
                    </span>
                  </div>

                  <div style={{ fontSize: 15, fontWeight: 600, color: '#0F172A', marginBottom: 6, lineHeight: 1.4 }}>
                    {dup.title}
                  </div>

                  {dup.ai_reason && (
                    <p style={{ fontSize: 13, color: '#78350F', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 10px', marginBottom: 10 }}>
                      {dup.ai_reason}
                    </p>
                  )}

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 12, color: '#475569', marginBottom: 10 }}>
                    <div><strong>Reporter:</strong> {dup.reporter_name || 'Admin'}</div>
                    <div><strong>Category:</strong> {dup.category}</div>
                    <div><strong>Published Date:</strong> {new Date(dup.published_at).toLocaleDateString()}</div>
                  </div>

                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ borderColor: '#0284C7', color: '#0284C7', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    onClick={() => setViewMatchedArticle(dup)}
                  >
                    <MdVisibility size={14} /> View Article
                  </button>
                </div>
              ))}

              {duplicateWarning.method === 'lexical_fallback' && (
                <p style={{ fontSize: 12, color: '#92400E', marginTop: 4 }}>
                  AI duplicate check was unavailable — showing keyword-similarity matches instead.
                </p>
              )}
            </div>

            <div className="modal-footer" style={{ padding: '16px 24px', background: '#F8FAFC', borderTop: '1px solid #E2E8F0', display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                className="btn btn-danger-solid"
                style={{ background: '#EF233C', color: '#ffffff', border: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                onClick={async () => {
                  const target = duplicateWarning.article;
                  setDeleting(true);
                  try {
                    await api.delete(`/admin/articles/${target.id}`);
                    toast.success('Article rejected successfully.');
                    setDuplicateWarning(null);
                    fetchArticles(pagination.page);
                  } catch (err) {
                    toast.error('Reject failed');
                  } finally {
                    setDeleting(false);
                  }
                }}
                disabled={deleting}
              >
                {deleting ? 'Rejecting...' : 'Reject Article'}
              </button>

              <button
                className="btn btn-primary"
                onClick={async () => {
                  const target = duplicateWarning.article;
                  setApproving(true);
                  try {
                    await api.put(`/admin/articles/${target.id}`, { status: 'published' });
                    toast.success('Article Approved & Published');
                    setDuplicateWarning(null);
                    fetchArticles(pagination.page);
                  } catch (err) {
                    toast.error('Approve failed');
                  } finally {
                    setApproving(false);
                  }
                }}
                disabled={approving}
              >
                {approving ? 'Publishing...' : 'Publish Anyway'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Matched Duplicate Article Single Preview Modal */}
      {viewMatchedArticle && (
        <div className="modal-overlay" onClick={() => setViewMatchedArticle(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640, borderRadius: 16, overflow: 'hidden' }}>
            <div className="modal-header" style={{ background: '#F8FAFC', padding: '16px 20px', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#0F172A' }}>
                Matched Duplicate Article Details (ID: #{viewMatchedArticle.id})
              </h3>
              <button className="modal-close" onClick={() => setViewMatchedArticle(null)}><MdClose /></button>
            </div>

            <div className="modal-body" style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#FFF1F2', border: '1px solid #FECDD3', padding: '10px 14px', borderRadius: 10, marginBottom: viewMatchedArticle.ai_reason ? 10 : 16 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#881337' }}>
                  {viewMatchedArticle.ai_reason ? 'AI Confidence' : 'Similarity Score'}
                </span>
                <span style={{ fontSize: 18, fontWeight: 800, color: '#E11D48' }}>
                  {viewMatchedArticle.ai_reason ? `${Math.round(viewMatchedArticle.ai_confidence * 100)}%` : `${viewMatchedArticle.score}%`} Match
                </span>
              </div>

              {viewMatchedArticle.ai_reason && (
                <p style={{ fontSize: 13, color: '#78350F', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '10px 12px', marginBottom: 16 }}>
                  {viewMatchedArticle.ai_reason}
                </p>
              )}

              {(viewMatchedArticle.image_url || viewMatchedArticle.thumbnail_url) && (
                <img
                  src={viewMatchedArticle.image_url || viewMatchedArticle.thumbnail_url}
                  alt=""
                  style={{ width: '100%', maxHeight: 240, objectFit: 'cover', borderRadius: 10, marginBottom: 16 }}
                />
              )}

              <h4 style={{ margin: '0 0 10px 0', fontSize: 16, fontWeight: 700, color: '#0F172A', lineHeight: 1.4 }}>
                {viewMatchedArticle.title}
              </h4>

              {viewMatchedArticle.summary && (
                <p style={{ fontSize: 14, color: '#334155', lineHeight: 1.5, marginBottom: 16, background: '#F8FAFC', padding: 12, borderRadius: 8, border: '1px solid #E2E8F0' }}>
                  {viewMatchedArticle.summary}
                </p>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 12.5, color: '#475569', background: '#FAFAFC', padding: 12, borderRadius: 10 }}>
                <div><strong>Article ID:</strong> #{viewMatchedArticle.id}</div>
                <div><strong>Reporter:</strong> {viewMatchedArticle.reporter_name || 'Admin'}</div>
                <div><strong>Category:</strong> {viewMatchedArticle.category || 'General'}</div>
                <div><strong>Published Date:</strong> {new Date(viewMatchedArticle.published_at).toLocaleDateString()}</div>
              </div>
            </div>

            <div className="modal-footer" style={{ padding: '14px 20px', background: '#F8FAFC', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {viewMatchedArticle.slug ? (
                <a
                  href={`http://localhost:3000/${viewMatchedArticle.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-ghost"
                  style={{ borderColor: '#0284C7', color: '#0284C7', fontSize: 13 }}
                >
                  Open Web Article ↗
                </a>
              ) : <div />}
              <button className="btn btn-primary" onClick={() => setViewMatchedArticle(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditWebPreview && (
        <WebPreviewModal
          article={editForm}
          onClose={() => setShowEditWebPreview(false)}
        />
      )}

      {editCropper.active && (
        <ImageCropper
          image={editCropper.image}
          onCropComplete={handleEditCropComplete}
          onCancel={() => setEditCropper({ active: false, image: null, type: null })}
        />
      )}
    </div>
  );
}
